
import React, { useState, useEffect } from "react";
import {
    Box, Grid, Typography, Paper, CircularProgress, Chip, Divider,
} from "@mui/material";
import InventoryIcon       from "@mui/icons-material/Inventory";
import EmailIcon           from "@mui/icons-material/Email";
import WorkOutlineIcon     from "@mui/icons-material/WorkOutline";
import QuestionAnswerIcon  from "@mui/icons-material/QuestionAnswer";
import TrendingUpIcon      from "@mui/icons-material/TrendingUp";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon  from "@mui/icons-material/HourglassEmpty";
import FiberNewIcon        from "@mui/icons-material/FiberNew";

import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
    AreaChart, Area,
} from "recharts";

import axiosInstance from "./Axiosinstance";

// ── palette ──────────────────────────────────────────────────────────────────
const COLORS = {
    blue:   "#1976d2",
    purple: "#7b1fa2",
    cyan:   "#0288d1",
    green:  "#2e7d32",
    orange: "#f57c00",
    red:    "#d32f2f",
    grey:   "#607d8b",
};

// ── helpers ───────────────────────────────────────────────────────────────────

/** Group array of objects by a date field into monthly buckets (last N months) */
const groupByMonth = (items = [], dateField = "createdAt", months = 6) => {
    const now   = new Date();
    const buckets = [];

    for (let i = months - 1; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({
            label: d.toLocaleString("default", { month: "short" }),
            year:  d.getFullYear(),
            month: d.getMonth(),
            count: 0,
        });
    }

    items.forEach((item) => {
        if (!item[dateField]) return;
        const d = new Date(item[dateField]);
        const bucket = buckets.find(
            (b) => b.month === d.getMonth() && b.year === d.getFullYear()
        );
        if (bucket) bucket.count += 1;
    });

    return buckets.map(({ label, count }) => ({ month: label, count }));
};

/** Count occurrences of a field value */
const countByField = (items = [], field) => {
    const map = {};
    items.forEach((item) => {
        const val = item[field] || "UNKNOWN";
        map[val] = (map[val] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
};

// ── sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color, sub, onClick }) => (
    <Paper
        onClick={onClick}
        elevation={0}
        sx={{
            p: 3, borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.07)",
            background: "#fff",
            cursor: onClick ? "pointer" : "default",
            transition: "all 0.2s ease",
            "&:hover": onClick ? {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                borderColor: `${color}40`,
            } : {},
        }}
    >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box>
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, mb: 1 }}>
                    {label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {value ?? <CircularProgress size={24} />}
                </Typography>
                {sub && (
                    <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
                        {sub}
                    </Typography>
                )}
            </Box>
            <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                background: `${color}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
            </Box>
        </Box>
        {onClick && (
            <Typography variant="caption" sx={{ color, fontWeight: 600, mt: 1.5, display: "block" }}>
                View all →
            </Typography>
        )}
    </Paper>
);

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", mb: 2, mt: 0.5 }}>
        {children}
    </Typography>
);

const ChartCard = ({ title, children, minHeight = 260 }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3, borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.07)",
            background: "#fff",
        }}
    >
        <Typography variant="body1" sx={{ fontWeight: 700, mb: 2.5, color: "text.primary" }}>
            {title}
        </Typography>
        <Box sx={{ minHeight }}>{children}</Box>
    </Paper>
);

// ── custom tooltip ─────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Paper elevation={3} sx={{ px: 2, py: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">{label}</Typography>
            {payload.map((p) => (
                <Typography key={p.name} variant="body2" fontWeight={700} sx={{ color: p.color }}>
                    {p.name}: {p.value}
                </Typography>
            ))}
        </Paper>
    );
};

// ── status badge row ───────────────────────────────────────────────────────────
const StatusRow = ({ label, count, color, icon }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
            <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
        </Box>
        <Chip
            label={count}
            size="small"
            sx={{ fontWeight: 700, fontSize: "12px", background: `${color}15`, color }}
        />
    </Box>
);

// ── main component ────────────────────────────────────────────────────────────

const AdminDashboardHome = ({ setActiveTab }) => {
    const [summary,      setSummary]      = useState(null);
    const [contacts,     setContacts]     = useState([]);
    const [inquiries,    setInquiries]    = useState([]);
    const [careers,      setCareers]      = useState([]);
    const [products,     setProducts]     = useState([]);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [loadingDetail,  setLoadingDetail]  = useState(true);

    // ── fetch summary ──────────────────────────────────────────────────────────
    useEffect(() => {
        axiosInstance.get("/api/admin/dashboard/summary")
            .then((res) => setSummary(res.data.data))
            .catch(() => setSummary({ totalProducts: 0, totalCareers: 0, totalContacts: 0, totalInquiries: 0 }))
            .finally(() => setLoadingSummary(false));
    }, []);

    // ── fetch detail lists for charts ──────────────────────────────────────────
    useEffect(() => {
        Promise.allSettled([
            axiosInstance.get("/api/admin/contacts"),
            axiosInstance.get("/api/admin/inquiries"),
            axiosInstance.get("/api/admin/careers"),
            axiosInstance.get("/api/admin/products"),
        ]).then(([c, i, ca, p]) => {
            if (c.status  === "fulfilled") setContacts(c.value.data.data   || []);
            if (i.status  === "fulfilled") setInquiries(i.value.data.data  || []);
            if (ca.status === "fulfilled") setCareers(ca.value.data.data   || []);
            if (p.status  === "fulfilled") setProducts(p.value.data.data   || []);
        }).finally(() => setLoadingDetail(false));
    }, []);

    // ── derived data ───────────────────────────────────────────────────────────

    // monthly trend — contacts + inquiries + careers combined
    const monthlyTrend = (() => {
        const months = 6;
        const now    = new Date();
        const buckets = [];
        for (let i = months - 1; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            buckets.push({
                month:    d.toLocaleString("default", { month: "short" }),
                year:     d.getFullYear(),
                monthIdx: d.getMonth(),
                contacts:  0,
                inquiries: 0,
                careers:   0,
            });
        }
        // const addTo = (items, key) =>
        //     items.forEach((item) => {
        //         if (!item.createdAt) return;
        //         const d = new Date(item.createdAt);
        //         const b = buckets.find(
        //             (bk) => bk.monthIdx === d.getMonth() && bk.year === d.getFullYear()
        //         );
        //         if (b) b[key] += 1;
        //     });
        // addTo(contacts,  "contacts");
        // addTo(inquiries, "inquiries");
        // addTo(careers,   "careers");
        const addTo = (items, key, dateField = "createdAt") =>
    items.forEach((item) => {
        if (!item[dateField]) return;

        const d = new Date(item[dateField]);

        const b = buckets.find(
            (bk) =>
                bk.monthIdx === d.getMonth() &&
                bk.year === d.getFullYear()
        );

        if (b) b[key] += 1;
    });
    addTo(contacts, "contacts");
addTo(inquiries, "inquiries");
addTo(careers, "careers", "appliedAt");
        return buckets.map(({ month, contacts, inquiries, careers }) => ({
            month, contacts, inquiries, careers,
        }));
    })();

    // contact status breakdown
    const contactStatusData = countByField(contacts, "status");

    // inquiry status breakdown
    const inquiryStatusData = countByField(inquiries, "status");

    // inquiry priority breakdown
    const priorityData = countByField(inquiries, "priority");

    // active vs inactive products
    const productStatusData = [
        { name: "Active",   value: products.filter((p) => p.isActive).length },
        { name: "Inactive", value: products.filter((p) => !p.isActive).length },
    ].filter((d) => d.value > 0);

    // contact status counts
    const cNew        = contacts.filter((c) => c.status === "NEW").length;
    const cRead       = contacts.filter((c) => c.status === "READ").length;
    const cResponded  = contacts.filter((c) => c.status === "RESPONDED").length;

    // inquiry status counts
    const iNew        = inquiries.filter((i) => i.status === "NEW").length;
    const iProgress   = inquiries.filter((i) => i.status === "IN_PROGRESS").length;
    const iResolved   = inquiries.filter((i) => i.status === "RESOLVED").length;
    const iClosed     = inquiries.filter((i) => i.status === "CLOSED").length;

    const PIE_COLORS_STATUS  = [COLORS.blue, COLORS.orange, COLORS.green, COLORS.grey];
    const PIE_COLORS_PRODUCT = [COLORS.green, COLORS.red];
    const PIE_COLORS_PRIORITY = [COLORS.green, COLORS.orange, COLORS.red, COLORS.purple];

    const statCards = [
        { id: "products",  label: "Total Products",       value: summary?.totalProducts,  icon: <InventoryIcon />,      color: COLORS.blue,   sub: `${products.filter(p => p.isActive).length} active` },
        { id: "contacts",  label: "Contact Enquiries",    value: summary?.totalContacts,  icon: <EmailIcon />,          color: COLORS.purple, sub: `${cNew} new` },
        { id: "inquiries", label: "Product Enquiries",    value: summary?.totalInquiries, icon: <QuestionAnswerIcon />, color: COLORS.cyan,   sub: `${iNew} new` },
        { id: "careers",   label: "Career Applications",  value: summary?.totalCareers,   icon: <WorkOutlineIcon />,    color: COLORS.green,  sub: `${careers.length} total` },
    ];

    return (
        <Box>
            {/* ── Welcome ── */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                    Welcome back 👋
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    Here's an overview of your platform activity.
                </Typography>
            </Box>

            {/* ── Stat Cards ── */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                {statCards.map((card) => (
                    <Grid item xs={12} sm={6} lg={3} key={card.id}>
                        <StatCard
                            icon={card.icon}
                            label={card.label}
                            value={loadingSummary ? null : card.value}
                            color={card.color}
                            sub={loadingDetail ? null : card.sub}
                            onClick={() => setActiveTab(card.id)}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* ── Activity Trend ── */}
            <SectionTitle>Activity Over Last 6 Months</SectionTitle>
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                <Grid item xs={12}>
                    <ChartCard title="Monthly Activity Trend" minHeight={280}>
                        {loadingDetail ? (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={monthlyTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        {[
                                            ["contacts",  COLORS.purple],
                                            ["inquiries", COLORS.cyan],
                                            ["careers",   COLORS.green],
                                        ].map(([key, color]) => (
                                            <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%"  stopColor={color} stopOpacity={0.15} />
                                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <RTooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                                    <Area type="monotone" dataKey="contacts"  name="Contacts"  stroke={COLORS.purple} fill={`url(#grad-contacts)`}  strokeWidth={2} dot={{ r: 3 }} />
                                    <Area type="monotone" dataKey="inquiries" name="Inquiries" stroke={COLORS.cyan}   fill={`url(#grad-inquiries)`} strokeWidth={2} dot={{ r: 3 }} />
                                    <Area type="monotone" dataKey="careers"   name="Careers"   stroke={COLORS.green}  fill={`url(#grad-careers)`}   strokeWidth={2} dot={{ r: 3 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </Grid>
            </Grid>

            {/* ── Status Breakdowns ── */}
            <SectionTitle>Status Breakdown</SectionTitle>
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>

                {/* Contact status pie */}
                {/* <Grid item xs={12} sm={6} lg={3}>
                    <ChartCard title="Contact Enquiry Status" minHeight={220}>
                        {loadingDetail ? (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={28} /></Box>
                        ) : (
                            <>
                                <ResponsiveContainer width="100%" height={160}>
                                    <PieChart>
                                        <Pie data={contactStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                                            {contactStatusData.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS_STATUS[i % PIE_COLORS_STATUS.length]} />
                                            ))}
                                        </Pie>
                                        <RTooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Divider sx={{ my: 1.5 }} />
                                <StatusRow label="New"       count={cNew}       color={COLORS.blue}   icon={<FiberNewIcon />} />
                                <StatusRow label="Read"      count={cRead}      color={COLORS.orange} icon={<HourglassEmptyIcon />} />
                                <StatusRow label="Responded" count={cResponded} color={COLORS.green}  icon={<CheckCircleIcon />} />
                            </>
                        )}
                    </ChartCard>
                </Grid> */}

                {/* Inquiry status pie */}
                {/* <Grid item xs={12} sm={6} lg={3}>
                    <ChartCard title="Inquiry Status" minHeight={220}>
                        {loadingDetail ? (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={28} /></Box>
                        ) : (
                            <>
                                <ResponsiveContainer width="100%" height={160}>
                                    <PieChart>
                                        <Pie data={inquiryStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                                            {inquiryStatusData.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS_STATUS[i % PIE_COLORS_STATUS.length]} />
                                            ))}
                                        </Pie>
                                        <RTooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Divider sx={{ my: 1.5 }} />
                                <StatusRow label="New"         count={iNew}      color={COLORS.blue}   icon={<FiberNewIcon />} />
                                <StatusRow label="In Progress" count={iProgress} color={COLORS.orange} icon={<HourglassEmptyIcon />} />
                                <StatusRow label="Resolved"    count={iResolved} color={COLORS.green}  icon={<CheckCircleIcon />} />
                                <StatusRow label="Closed"      count={iClosed}   color={COLORS.grey}   icon={<CheckCircleIcon />} />
                            </>
                        )}
                    </ChartCard>
                </Grid> */}

                {/* Inquiry priority bar */}
                {/* <Grid item xs={12} sm={6} lg={3}>
                    <ChartCard title="Inquiry Priority" minHeight={220}>
                        {loadingDetail ? (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={28} /></Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={priorityData} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <RTooltip content={<CustomTooltip />} />
                                    <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
                                        {priorityData.map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS_PRIORITY[i % PIE_COLORS_PRIORITY.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </Grid> */}

                {/* Products active/inactive */}
                <Grid item xs={12} sm={6} lg={3}>
                    <ChartCard title="Product Status" minHeight={220}>
                        {loadingDetail ? (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={28} /></Box>
                        ) : (
                            <>
                                <ResponsiveContainer width="100%" height={160}>
                                    <PieChart>
                                        <Pie data={productStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                                            {productStatusData.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS_PRODUCT[i]} />
                                            ))}
                                        </Pie>
                                        <RTooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Divider sx={{ my: 1.5 }} />
                                <StatusRow label="Active"   count={products.filter(p => p.isActive).length}  color={COLORS.green} icon={<CheckCircleIcon />} />
                                <StatusRow label="Inactive" count={products.filter(p => !p.isActive).length} color={COLORS.red}   icon={<HourglassEmptyIcon />} />
                            </>
                        )}
                    </ChartCard>
                </Grid>
            </Grid>

            {/* ── Applications bar chart ── */}
            <SectionTitle>Career Applications — Monthly</SectionTitle>
            <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                    <ChartCard title="Applications per Month" minHeight={240}>
                        {loadingDetail ? (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}><CircularProgress size={32} /></Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                {/* <BarChart data={groupByMonth(careers)} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}> */}
                                <BarChart
    data={groupByMonth(careers, "appliedAt")}
    margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <RTooltip content={<CustomTooltip />} />
                                    <Bar dataKey="count" name="Applications" fill={COLORS.green} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </Grid>

                {/* Contacts bar chart */}
                <Grid item xs={12} md={6}>
                    <ChartCard title="Contact Enquiries per Month" minHeight={240}>
                        {loadingDetail ? (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}><CircularProgress size={32} /></Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={groupByMonth(contacts)} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <RTooltip content={<CustomTooltip />} />
                                    <Bar dataKey="count" name="Enquiries" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AdminDashboardHome;
